import { HttpClient } from "@angular/common/http";
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import {
  Events,
  LoadingController,
  NavParams,
  ToastController,
  ViewController,
} from "ionic-angular";
import * as _ from "lodash";
import ContentWrapper from "models/content-wrapper/content-wrapper";
import SortOptions from "models/content-wrapper/sort-options";
import Facility from "models/facilities/facility";
import { FacilityDetail } from "models/facilities/facility-details";
import { PatientChartResponse } from "models/patient-chart/patientChartResponse";
import CompanyResourceSummary from "models/resource-management/company-resource-summary";
import CompanyResourceType from "models/resource-management/company-resource-type.enum";
import CredentialedResource from "models/resource-management/credentialed-resource";
import Physician from "models/resource-management/physician";
import * as moment from "moment-timezone";
import { CareTeamsService } from "providers/care-teams-service/care-teams-service";
import {
  EthnicityRaceSyncConfig,
  EthnicityRaceSyncService,
  SyncState,
} from "providers/ethnicity-race-sync/ethnicity-race-sync.service";
import { ResourceManagementService } from "providers/resource-management/resource-management.service";
import { UtilityService } from "providers/utility-service/utility-service";
import { Observable, Subject, Subscription } from "rxjs";
import { forkJoin } from "rxjs/observable/forkJoin";
import { of } from "rxjs/observable/of";
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
} from "rxjs/operators";
import { ContactDetails } from "../../../models/contact/contactDetails";
import LookupValue from "../../../models/patient-chart/lookupValue";
import { Patient } from "../../../models/patient-chart/patient";
import { commonVar } from "../../../providers/common-var/common";
import { FacilitiesService } from "../../../providers/facilities-service/facilities.service";
import { PatientChartService } from "../../../providers/patient-charts/patient-chart.service";
import { ReferralsServiceProvider } from "../../../providers/patient-charts/referrals-service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import {
  patientDetailsValidationMessage,
  referralLocationValidationMessage,
} from "../../../validators/validationMessages";
import { IntakePatientProfileTab } from "../models/intake-patient-profile-type";

interface CompanyResourceDropdownItem {
  address: ContactDetails;
  firstName: string;
  label: string;
  lastName: string;
  phoneNumber: string;
  value: number;
}

interface PhysicianCompanyResource {
  id?: number;
  externalId?: number;
  firstName?: string;
  lastName?: string;
  middleInitial?: string;
  credentials?: string;
  displayName?: string;
  createdDatetime?: Date;
  npi?: string;
  dea?: string;
  deaExpireDate?: string;
  isSelected?: boolean;
  isLoaded?: boolean;
  phoneNumber?: string;
  faxNumber?: string;
  trainingCompletionDate?: Date;
  status?: string;
  hasCredentials?: boolean;
  createdBy?: string;
  $provider?: Observable<CredentialedResource>;
  locations?: any;
  showMore?: boolean;
  professionalRelation?: string;
  cognitoUserId?: number;
  isSystemUser?: boolean;
  disciplines?: string;
  contactId?: number;
  mobile?: string;
  fax?: string;
  work?: string;
  address?: ContactDetails;
  contactType?: string;
  emailAddress?: string;
  situationNotes?: string;
  selectedCredential?: string;
  relationType?: LookupValue;
  referralType?: LookupValue;
  referralCreditType?: LookupValue;
}

@Component({
  selector: "patient-details-grid",
  templateUrl: "patient-details-grid.html",
})
export class PatientDetailsGridComponent implements OnInit, OnDestroy {
  private ethnicityRaceSyncSubscriptions: Subscription[] = [];
  private syncState: SyncState;

  @Output() valueChange: EventEmitter<{
    patientChartId: number;
    patientExternalId: number;
  }> = new EventEmitter<{
    patientChartId: number;
    patientExternalId: number;
  }>();

  @Input() patientChartId: number;
  @Input() lookUpValues: any[] = [];
  @Input() resourceManagementLookupValues: Record<string, LookupValue[]> = {};
  @Input() patientChartsData: any = null;
  @Input() patientDataPhysicians: Physician[] = [];
  @Input() tabStatuses: { [key in IntakePatientProfileTab]?: string };

  originalDetailsFormValues: any;
  originalReferralLocationFormValues: any;
  originalReferralCreditFormValues: any;
  orderEntryToggle: boolean = false;
  spinner = null;
  spinnerCounter = 0;
  enableCreateReferralLocation: boolean = false;
  enableFillReferralLocation: boolean = false;
  showCloseIcon: boolean = false;
  locationOptions: Facility[] = [];
  newBillingFacilityToggle: boolean = false;
  locationInput$ = new Subject<string>();
  facilityOptions: Facility[] = [];
  showCurrentLocation = false;
  typeSubscription: Subscription;
  sameAsSubscription: Subscription;
  locationChangeSubscription: Subscription;
  locationValueChangeSubscription: Subscription;
  detailsFormGroup: FormGroup;
  referralLocationFormGroup: FormGroup;
  referralCreditFormGroup: FormGroup;
  locationsLoading = false;
  referralContactAdministration: string = "Create New Referral Location";
  careTypeList = [{ label: "Hospice", value: "hospice" }];
  genderList = [
    { label: "Female", value: 2 },
    { label: "Male", value: 1 },
  ];
  veteranList = [
    { label: "Yes", value: true },
    { label: "No", value: false },
  ];
  subscriptions = new Subscription();
  maritalStatuses = [];
  religions = [];
  ethnicities: LookupValue[];
  hopeEthnicities: LookupValue[];
  hopeRaces: LookupValue[];
  countries = commonVar.COUNTRIES;
  stateList = commonVar.STATE_LIST;
  firstLanguages: LookupValue[];
  prefixes: LookupValue[];
  suffixes: LookupValue[];
  careLocationTypes: LookupValue[];
  hasValidZip: boolean = false;
  hasValidZipReferral: boolean = false;
  hasValidZipReferralCredit: boolean = false;
  startDateMin: any;
  startDateMax: any;
  patientDataInfo: any;
  hideCounty: boolean = true;
  hideCountyReferral: boolean = true;
  hideCountyReferralCredit: boolean = true;
  countyOption: any;
  countyOptionReferral: any;
  countyOptionReferralCredit: any;
  countyFetchError: string = "";
  countyFetchErrorReferral: string = "";
  countyFetchErrorReferralCredit: string = "";
  noCountiesReturned: boolean = false;
  noCountiesReturnedReferral: boolean = false;
  noCountiesReturnedReferralCredit: boolean = false;
  patientChart$: Observable<Patient>;
  zipCodeMask = commonVar.ZIP_CODE_MASK;
  phoneMask = commonVar.PHONE_US_MASK;
  phoneUsPlaceHolder = commonVar.PHONE_US_PLACEHOLDER;
  specialCharacters = commonVar.SPECIAL_CHAR;
  homeSelected = false;
  duplicatePatient = false;
  careTeams: any[];
  duplicatePatientSSN: boolean = false;
  facilityLocation = [
    { id: 1, display: "Home", value: "Home" },
    {
      id: 8,
      display: "Assisted Living Facility",
      value: "ASSISTED_LIVING_FACILITY",
    },
    { id: 2, display: "Nursing Home", value: "NURSING_HOME" },
    {
      id: 7,
      display: "Skilled Nursing Facility",
      value: "SKILLED_NURSING_FACILITY",
    },
    { id: 6, display: "In-patient - Hospital", value: "INPATIENT_HOSPITAL" },
    {
      id: 5,
      display: "In-patient - Hospice Facility",
      value: "INPATIENT_HOSPICE_FACILITY",
    },
    {
      id: 9,
      display: "Long Term Care - Hospital",
      value: "LONG_TERM_CARE_HOSPITAL",
    },
    {
      id: 11,
      display: "In-patient Psychiatric Facility",
      value: "INPATIENT_PSYCHIATRIC_FACILITY",
    },
    { id: 10, display: "Unspecified", value: "UNSPECIFIED" },
    {
      id: 12,
      display: "Hospice Residential Facility",
      value: "HOSPICE_RESIDENTIAL_FACILITY",
    },
  ];
  ssnMask = commonVar.SSN_MASK;
  zipCodeExtMask = commonVar.ZIP_CODE_EXTENSION_MASK;
  emailRegex = commonVar.EMAIL_REGEX;
  phoneRegex = commonVar.PHONE_REGEX;
  zipRegex = commonVar.ZIP_PATTERN;
  notesUrl: string;
  headers: any;
  referralCreditTypes: LookupValue[] = [];
  referralCreditRelation: LookupValue[] = [];
  referralCreditContact: Physician;
  reinstatedReferral: boolean = false;
  initialPatientStatusType: string = null;
  isCancelledPatient: boolean = false;
  showProviderOrCommunityResourceSearchBox: boolean = false;
  labelForProviderOrCommunityResourceSearchBox: string = "";
  placeholderForProviderOrCommunityResourceSearchBox: string = "";
  dropdownItemsProviderOrCommunityResource$: Observable<
    CompanyResourceDropdownItem[]
  >;
  queryProviderOrCommunityResource$ = new Subject<string>();
  currentProviderOrCommunityResourceId = 0;
  listProviders: any[] = [];
  filteredProvidersOrCommunityResources: Array<CompanyResourceDropdownItem> =
    [];
  allProvidersOrCommunityResourcesObservable$: Observable<
    Array<CompanyResourceSummary>
  >;
  allProvidersOrCommunityResources: Array<CompanyResourceSummary> = [];

  constructor(
    private formBuilder: FormBuilder,
    public stateData: StateManagementService,
    public patientChartService: PatientChartService,
    private facilitiesService: FacilitiesService,
    public referralService: ReferralsServiceProvider,
    public viewController: ViewController,
    public loadingCtrl: LoadingController,
    private toastCtrl: ToastController,
    public http: HttpClient,
    private resourceManagementService: ResourceManagementService,
    public utilityService: UtilityService,
    private careTeamsService: CareTeamsService,
    public navParams: NavParams,
    public events: Events,
    private ethnicityRaceSyncService: EthnicityRaceSyncService
  ) {
    this.initializeForm();
    this.stateData.newBillingFacility.subscribe((x) => {
      this.newBillingFacilityToggle =
        x != null ? x : this.newBillingFacilityToggle;
    });
  }

  ngOnInit(): void {
    this.patientDataInfo = this.patientChartsData;
    this.reinstatedReferral =
      this.navParams.data.reinstatedReferral ??
      localStorage.getItem("reinstatedReferral") === "true";
    this.isCancelledPatient =
      this.patientDataInfo?.patientStatus?.toLowerCase() === "cancelled";
    this.initializeBirthDateRange();
    this.subscribeToChanges();
    this.setLookUpValues();
    this.setupNoneOptionForPrefixAndSuffix();
    this.setupLocationInputListener();
    this.loadOrderEntryToggle();
    this.loadCareTeams();
    this.amendForm();
    this.onSearchForProviderOrCommunityResourceClick();
    this.setupEthnicityRaceSyncHandling();
  }

  private setupEthnicityRaceSyncHandling(): void {
    const config: EthnicityRaceSyncConfig = {
      ethnicityControlName: "ethnicity",
      hopeEthnicityControlName: "hopeEthnicity",
      hopeRaceControlName: "hopeRace",
      ethnicityItems: this.ethnicities,
      hopeEthnicityItems: this.hopeEthnicities,
      hopeRaceItems: this.hopeRaces,
    };

    const { subscriptions, syncState } =
      this.ethnicityRaceSyncService.setupSynchronization(
        this.detailsFormGroup,
        config
      );

    this.ethnicityRaceSyncSubscriptions = subscriptions;
    this.syncState = syncState;
  }

  ngOnChanges(changes): void {
    if (changes.patientChartsData?.currentValue) {
      this.patientDataInfo = changes.patientChartsData.currentValue;
      if (Object.keys(this.patientDataInfo).length) {
        this.amendForm();
      }
    }
  }

  setupNoneOptionForPrefixAndSuffix(): void {
    const prefixSubscription = this.detailsFormGroup
      .get("prefix")
      .valueChanges.subscribe((newValue) => {
        if (newValue == 7) this.detailsFormGroup.get("prefix").setValue(null);
      });
    const suffixSubscription = this.detailsFormGroup
      .get("suffix")
      .valueChanges.subscribe((newValue) => {
        if (newValue == 9) this.detailsFormGroup.get("suffix").reset(null);
      });
    this.subscriptions.add(prefixSubscription);
    this.subscriptions.add(suffixSubscription);
  }

  checkSSNUniqueness(
    control: AbstractControl
  ): Observable<ValidationErrors | null> {
    let deMasked = this.removeMask(control.value) || "";
    if (deMasked.length !== 9) {
      this.duplicatePatientSSN = false;
      this.duplicatePatient = false;
      return Observable.of(null);
    }

    return this.patientChartService
      .checkDuplicateUniqueIdentifier(deMasked)
      .take(1)
      .map((exists) => {
        if (exists) {
          const patientData = this.patientDataInfo;
          const ssn = patientData?.patient?.uniqueIdentifier;

          if (ssn === deMasked) {
            this.duplicatePatientSSN = false;
            this.duplicatePatient = false;
            return null;
          }

          this.duplicatePatientSSN = true;
          this.referralService.presentToast("SSN is not unique");
          return { notUnique: true };
        } else {
          this.duplicatePatientSSN = false;
          this.duplicatePatient = false;
          return null;
        }
      });
  }

  amendForm() {
    const patientData = this.patientDataInfo;
    const areAddressesEqual = (address1: any, address2: any): boolean => {
      if (!address1 || !address2) {
        return false;
      }
      return (
        address1.address1 === address2.address1 &&
        address1.address === address2.address &&
        address1.city === address2.city &&
        address1.state === address2.state &&
        address1.zip === address2.zip &&
        address1.zipExtension === address2.zipExtension &&
        address1.county === address2.county
      );
    };
    const sameAsHomeAddress =
      patientData.patient && patientData.referralAddress
        ? areAddressesEqual(
            patientData.patient.homeAddress,
            patientData.referralAddress
          )
        : false;

    this.showCurrentLocation = !!patientData.referralAddress;
    const referralAddress = {
      referralName:
        patientData.referralAddress && patientData.referralAddress.name
          ? patientData.referralAddress.name
          : null,
      address1Referral:
        patientData.referralAddress && patientData.referralAddress.address1
          ? patientData.referralAddress.address1
          : null,
      referralCity:
        patientData.referralAddress && patientData.referralAddress.city
          ? patientData.referralAddress.city
          : null,
      referralState:
        patientData.referralAddress && patientData.referralAddress.state
          ? patientData.referralAddress.state
          : null,
      referralZip:
        patientData.referralAddress && patientData.referralAddress.zip
          ? patientData.referralAddress.zip
          : null,
      referralZipExtension:
        patientData.referralAddress && patientData.referralAddress.zipExtension
          ? patientData.referralAddress.zipExtension
          : null,
      referralPhone:
        patientData.referralAddress && patientData.referralAddress.phone
          ? this.applyMask(
              patientData.referralAddress.phone ||
                patientData.patient.phoneNumber.number,
              this.phoneMask
            )
          : null,
      referralCounty:
        patientData.referralAddress && patientData.referralAddress.county
          ? patientData.referralAddress.county
          : null,
      referralEmailAddress:
        patientData.referralAddress && patientData.referralAddress.email
          ? patientData.referralAddress.email
          : null,
      sameAsHomeAddress: sameAsHomeAddress,
      locationType: patientData.referralAddress?.locationTypeId
        ? patientData.referralAddress.locationTypeId
        : null,
      locationTypeValueCheck: null,
      locationTypeValue: patientData.referralAddress?.name
        ? patientData.referralAddress.name
        : null,
    };

    this.referralCreditContact = this.patientDataPhysicians.find(
      (physician) => physician.contactType === "REFERRAL_CREDIT"
    );
    if (this.referralCreditContact) {
      this.filteredProvidersOrCommunityResources = [
        this.referralCreditContact,
      ].map((resource) => {
        return {
          label: resource.getFullName(),
          value: resource.id,
          firstName: resource.firstName,
          lastName: resource.lastName,
          phoneNumber: resource.work,
          address: resource.address,
        };
      });
    }
    const referralCreditData = {
      referralCreditType:
        this.referralCreditContact &&
        this.referralCreditContact.referralCreditType
          ? this.referralCreditContact.referralCreditType.id
          : null,
      referralCreditRelation:
        this.referralCreditContact && this.referralCreditContact.relationType
          ? this.referralCreditContact.relationType.id
          : null,
      referralCreditProviderOrCommunityResource:
        this.referralCreditContact && this.referralCreditContact.id
          ? this.referralCreditContact.id
          : null,
      referralCreditFirstName: this.referralCreditContact
        ? this.referralCreditContact.firstName
        : null,
      referralCreditLastName: this.referralCreditContact
        ? this.referralCreditContact.lastName
        : null,
      referralCreditPhoneNumber: this.referralCreditContact
        ? this.referralCreditContact.work
        : null,
      referralCreditStreetAddress:
        this.referralCreditContact && this.referralCreditContact.address
          ? this.referralCreditContact.address.address1
          : null,
      referralCreditCity:
        this.referralCreditContact && this.referralCreditContact.address
          ? this.referralCreditContact.address.city
          : null,
      referralCreditState:
        this.referralCreditContact && this.referralCreditContact.address
          ? this.referralCreditContact.address.state
          : null,
      referralCreditZip:
        this.referralCreditContact && this.referralCreditContact.address
          ? this.referralCreditContact.address.zip
          : null,
      referralCreditZipExtension:
        this.referralCreditContact && this.referralCreditContact.address
          ? this.referralCreditContact.address.zipExtension
          : null,
      referralCreditCounty:
        this.referralCreditContact && this.referralCreditContact.address
          ? this.referralCreditContact.address.county
          : null,
    };
    this.initializeProviderOrCommunityResourceSearchBox(
      this.referralCreditContact?.relationType?.id
    );
    const patientStatus = patientData.patientStatus?.toLowerCase() ?? null;
    this.initialPatientStatusType = this.reinstatedReferral
      ? "referral"
      : patientStatus;
    const detailsFormValues = Object.assign({
      type: this.initialPatientStatusType,
      careType: patientData.careType
        ? patientData.careType.toLowerCase()
        : null,
      nickname:
        patientData.patient && patientData.patient.nickName
          ? patientData.patient.nickName
          : null,
      dateOfBirth:
        patientData.patient && patientData.patient.birthDate
          ? patientData.patient.birthDate
          : null,
      ssn:
        patientData.patient && patientData.patient.uniqueIdentifier
          ? this.applyMask(patientData.patient.uniqueIdentifier, this.ssnMask)
          : null,
      unknownSSN: !!(patientData.patient && patientData.patient.ssnUnknown),
      address1:
        patientData.patient &&
        patientData.patient.homeAddress &&
        patientData.patient.homeAddress.address1
          ? patientData.patient.homeAddress.address1
          : "",
      address:
        patientData.patient &&
        patientData.patient.homeAddress &&
        patientData.patient.homeAddress.address
          ? patientData.patient.homeAddress.address
          : "",
      city:
        patientData.patient &&
        patientData.patient.homeAddress &&
        patientData.patient.homeAddress.city
          ? patientData.patient.homeAddress.city
          : "",
      state:
        patientData.patient &&
        patientData.patient.homeAddress &&
        patientData.patient.homeAddress.state
          ? patientData.patient.homeAddress.state
          : null,
      firstName:
        patientData.patient && patientData.patient.firstName
          ? patientData.patient.firstName
          : "",
      middleInitial:
        patientData.patient && patientData.patient.middleInitial
          ? patientData.patient.middleInitial
          : "",
      lastName:
        patientData.patient && patientData.patient.lastName
          ? patientData.patient.lastName
          : "",
      prefix:
        patientData.patient &&
        patientData.patient.prefix &&
        patientData.patient.prefix.id
          ? patientData.patient.prefix.id
          : null,
      suffix:
        patientData.patient &&
        patientData.patient.suffix &&
        patientData.patient.suffix.id
          ? patientData.patient.suffix.id
          : null,
      zip:
        patientData.patient &&
        patientData.patient.homeAddress &&
        patientData.patient.homeAddress.zip
          ? patientData.patient.homeAddress.zip
          : "",
      zipExtension:
        patientData.patient &&
        patientData.patient.homeAddress &&
        patientData.patient.homeAddress.zipExtension
          ? patientData.patient.homeAddress.zipExtension
          : "",
      county:
        patientData.patient &&
        patientData.patient.homeAddress &&
        patientData.patient.homeAddress.county
          ? patientData.patient.homeAddress.county
          : "",
      phoneNumber:
        patientData.patient &&
        patientData.patient.phoneNumber &&
        patientData.patient.phoneNumber.number
          ? this.applyMask(
              patientData.patient.phoneNumber.number,
              this.phoneMask
            )
          : null,
      emailAddress:
        patientData.patient && patientData.patient.emailAddress
          ? patientData.patient.emailAddress
          : "",
      gender:
        patientData.patient &&
        patientData.patient.gender &&
        patientData.patient.gender.id
          ? patientData.patient.gender.id
          : null,
      maritalStatus:
        patientData.patient &&
        patientData.patient.maritalStatus &&
        patientData.patient.maritalStatus.id
          ? patientData.patient.maritalStatus.id
          : null,
      veteran:
        patientData.patient && patientData.patient.hasOwnProperty("veteran")
          ? patientData.patient.veteran
          : null,
      firstLanguage:
        patientData.patient &&
        patientData.patient.firstLanguage &&
        patientData.patient.firstLanguage.id
          ? patientData.patient.firstLanguage.id
          : null,
      religion:
        patientData.patient &&
        patientData.patient.religion &&
        patientData.patient.religion.id
          ? patientData.patient.religion.id
          : null,
      religionOtherText:
        patientData.patient && patientData.patient.religionOtherText
          ? patientData.patient.religionOtherText
          : "",
      ethnicity:
        patientData.patient &&
        patientData.patient.ethnicities &&
        this.ethnicities
          ? this.getMatchingEthnicitiesIdsAndRacesIdsByDescription(
              patientData.patient.ethnicities,
              this.ethnicities,
              "ethnicity"
            )
          : [],
      hopeEthnicity:
        patientData.patient &&
        patientData.patient.hopeEthnicities &&
        this.hopeEthnicities
          ? this.getMatchingEthnicitiesIdsAndRacesIdsByDescription(
              patientData.patient.hopeEthnicities,
              this.hopeEthnicities,
              "hopeEthnicity"
            )
          : [],
      hopeRace:
        patientData.patient && patientData.patient.hopeRaces && this.hopeRaces
          ? this.getMatchingEthnicitiesIdsAndRacesIdsByDescription(
              patientData.patient.hopeRaces,
              this.hopeRaces,
              "hopeRace"
            )
          : [],
      intakeNotes:
        patientData.intakeNote && patientData.intakeNote.note
          ? patientData.intakeNote.note
          : null,
      team: patientData.teamInfo ? patientData.teamInfo.team.id : null,
    });

    if (sameAsHomeAddress) {
      this.referralLocationFormGroup.get("locationType").disable();
      this.referralLocationFormGroup.get("locationTypeValue").disable();
      this.showCurrentLocation = true;
    } else if (!this.referralLocationFormGroup.get("locationType").value) {
      this.enableFillReferralLocation = true;
      this.referralLocationFormGroup.get("referralState").disable();
    }
    this.cloneReferralLocationFromForm();
    this.detailsFormGroup.setValue(detailsFormValues, { emitEvent: false });
    this.referralLocationFormGroup.setValue(referralAddress, {
      emitEvent: false,
    });
    this.referralCreditFormGroup.setValue(referralCreditData, {
      emitEvent: false,
    });

    if (detailsFormValues.ssn) {
      this.detailsFormGroup.get("ssn").markAsDirty();
    }

    this.referralService.PATIENT_STATUS = "referral";
    let patientChartInfo = {
      id: patientData.externalId,
      externalId: false,
      patientChartId: patientData.id,
      fullName:
        patientData.patient && patientData.patient.fullName
          ? patientData.patient.fullName
          : "",
    };
    this.originalDetailsFormValues = _.cloneDeep(this.detailsFormGroup.value);
    this.originalReferralLocationFormValues = _.cloneDeep(
      this.referralLocationFormGroup.value
    );
    this.originalReferralCreditFormValues = _.cloneDeep(
      this.referralCreditFormGroup.value
    );

    window.sessionStorage.setItem(
      "patientChartInfo",
      JSON.stringify(patientChartInfo)
    );
    this.events.publish("patientChartIdChange", patientChartInfo);
  }

  private parseHopeDescriptions(
    sourceDescription: string,
    items: any[]
  ): number[] {
    if (!sourceDescription || !items) {
      return [];
    }

    const matchedIds: number[] = [];
    const availableDescriptions = items.map((item) => item.description);

    // Sort descriptions by length (longest first) to match longer descriptions first
    const sortedDescriptions = availableDescriptions.sort(
      (a, b) => b.length - a.length
    );

    let remainingText = sourceDescription;

    for (const description of sortedDescriptions) {
      if (remainingText.includes(description)) {
        const item = items.find((i) => i.description === description);
        if (item && !matchedIds.includes(item.id)) {
          matchedIds.push(item.id);
          // Remove the matched description from remaining text
          remainingText = remainingText
            .replace(description, "")
            .replace(/,\s*,/g, ",")
            .trim();
          // Clean up leading/trailing commas
          remainingText = remainingText.replace(/^,\s*|,\s*$/g, "");
        }
      }
    }

    return matchedIds;
  }

  applyMask(value, mask) {
    if (!value) {
      return value;
    }
    let values = value.split("");
    let lastIndex;
    lastIndex = 0;
    mask = mask.split("");
    mask.forEach((val, index) => {
      if (this.specialCharacters.indexOf(val) === -1) {
        mask[index] = values[lastIndex];
        lastIndex += 1;
      }
    });
    return mask.join("");
  }

  private initializeBirthDateRange(): void {
    this.startDateMin = { year: 1900, month: 1, day: 1 };
    this.startDateMax = {
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      day: new Date().getDate(),
    };
  }

  private subscribeToChanges(): void {
    this.subscribeToSameAsChanges();
    this.subscribeToLocationTypeChanges();
    this.subscribeToLocationTypeValueChanges();
    this.subscribeToPalliativeChanges();
  }

  private subscribeToPalliativeChanges(): void {
    const hasPalliativeSub = this.stateData.hasPalliative.subscribe((value) => {
      if (value) {
        this.careTypeList.push({ label: "Palliative", value: "palliative" });
      }
    });
    this.subscriptions.add(hasPalliativeSub);
  }

  private setupLocationInputListener(): void {
    this.locationInput$
      .pipe(
        filter(() => this.referralLocationFormGroup.get("locationType").value),
        tap(() => (this.locationsLoading = true)),
        switchMap((term) => {
          if (!term) {
            this.locationsLoading = false;
            this.showCurrentLocation = false;
            return [];
          }

          if (this.facilityOptions && this.facilityOptions.length === 0) {
            this.mapFacilityAndReferralLocation(
              this.referralLocationFormGroup.get("locationType").value
            );
          }
          if (this.facilityOptions && this.facilityOptions.length !== 0) {
            this.enableCreateReferralLocation = false;
            const uniqueFacilities = _.uniqBy(
              this.facilityOptions,
              (f: any) =>
                `${(f.name || "").toLowerCase().trim()}|${(
                  f.address1 ||
                  f.address?.address1 ||
                  ""
                )
                  .toLowerCase()
                  .trim()}|${(f.city || f.address?.city || "")
                  .toLowerCase()
                  .trim()}|${(f.state || f.address?.state || "")
                  .toLowerCase()
                  .trim()}|${(f.zip || f.address?.zip || "")
                  .toLowerCase()
                  .trim()}`
            );
            this.locationOptions = uniqueFacilities.filter((facility) =>
              facility.name
                .toLocaleLowerCase()
                .includes(term.toLocaleLowerCase())
            );
            this.locationOptions.push({
              name: "New Referral Location",
              id: -1,
              address: new ContactDetails(),
              facilityType: "Referral",
            });
          } else {
            this.locationOptions = [];
            this.locationOptions.push({
              name: "New Referral Location",
              id: -1,
              address: new ContactDetails(),
              facilityType: "Referral",
            });
          }
          return [];
        })
      )
      .subscribe();
  }

  private loadOrderEntryToggle(): void {
    this.stateData.showOrderEntryOM.subscribe((x) => {
      if (x !== null) {
        this.orderEntryToggle = x;
      }
    });
  }

  get notFoundText(): string {
    return this.locationsLoading
      ? "Searching locations..."
      : "No locations found.";
  }

  ngOnDestroy(): void {
    if (this.typeSubscription) {
      this.typeSubscription.unsubscribe();
    }

    if (this.sameAsSubscription) {
      this.sameAsSubscription.unsubscribe();
    }

    if (this.locationChangeSubscription) {
      this.locationChangeSubscription.unsubscribe();
    }

    if (this.locationValueChangeSubscription) {
      this.locationValueChangeSubscription.unsubscribe();
    }

    this.enableCreateReferralLocation = false;
    this.subscriptions.unsubscribe();
  }

  initializeForm() {
    this.detailsFormGroup = this.formBuilder.group({
      type: ["", Validators.required],
      careType: [null, Validators.required],
      firstName: [null, Validators.required],
      middleInitial: [null],
      lastName: [null, Validators.required],
      prefix: [null],
      suffix: [null],
      nickname: [null],
      dateOfBirth: [null, Validators.required],
      ssn: [""],
      unknownSSN: [false],
      address1: [""],
      address: [null],
      city: [""],
      state: [null],
      zip: [""],
      zipExtension: [""],
      county: [null],
      phoneNumber: [null, [Validators.pattern(this.phoneRegex)]],
      emailAddress: [null, Validators.pattern(this.emailRegex)],
      gender: [null],
      maritalStatus: [null],
      veteran: [null],
      firstLanguage: [null],
      religion: [null],
      religionOtherText: [null],
      ethnicity: [[]],
      hopeEthnicity: [[]],
      hopeRace: [[]],
      intakeNotes: [null],
      team: [null],
    });
    this.referralLocationFormGroup = this.formBuilder.group({
      sameAsHomeAddress: [false],
      locationTypeValueCheck: [null],
      referralZipExtension: [null],
      referralPhone: [null],
      locationTypeValue: [null],
      locationType: [null],
      referralName: [null, Validators.required], // Location Name
      address1Referral: [null, Validators.required],
      referralCity: [null, Validators.required],
      referralState: [null, Validators.required],
      referralZip: [
        null,
        [Validators.required, Validators.pattern(this.zipRegex)],
      ],
      referralCounty: [null, Validators.required],
      referralEmailAddress: [null, Validators.pattern(this.emailRegex)],
    });
    this.referralCreditFormGroup = this.formBuilder.group({
      referralCreditType: [null, Validators.required],
      referralCreditRelation: [null, Validators.required],
      referralCreditProviderOrCommunityResource: [null],
      referralCreditFirstName: [null, Validators.required],
      referralCreditLastName: [null, Validators.required],
      referralCreditPhoneNumber: [null],
      referralCreditStreetAddress: [null],
      referralCreditCity: [null],
      referralCreditState: [null],
      referralCreditZip: [null],
      referralCreditZipExtension: [null],
      referralCreditCounty: [null],
    });
    const ssnControl = this.detailsFormGroup.get("ssn");
    ssnControl.setAsyncValidators(() => this.checkSSNUniqueness(ssnControl));
  }

  unknownSSNChanged(event) {
    if (event.checked) {
      const ssnControl = this.detailsFormGroup.get("ssn");
      ssnControl.setValue("");
    }
  }

  removeMask(maskNumber) {
    if (!maskNumber) {
      return maskNumber;
    } else {
      return maskNumber.replace(/\D+/g, "");
    }
  }

  cloneReferralLocationFromForm() {
    if (this.enableCreateReferralLocation) {
      this.patientDataInfo.referralAddress = new ContactDetails();
      this.patientDataInfo.referralAddress.address1 =
        this.referralLocationFormGroup.get("address1Referral").value;
      this.patientDataInfo.referralAddress.city =
        this.referralLocationFormGroup.get("referralCity").value;
      this.patientDataInfo.referralAddress.state =
        this.referralLocationFormGroup.get("referralState").value;
      this.patientDataInfo.referralAddress.zip =
        this.referralLocationFormGroup.get("referralZip").value;
      this.patientDataInfo.referralAddress.zipExtension =
        this.referralLocationFormGroup.get("referralZipExtension").value;
      this.patientDataInfo.referralAddress.county =
        this.referralLocationFormGroup.get("referralCounty").value;
      this.patientDataInfo.referralAddress.name =
        this.referralLocationFormGroup.get("referralName").value;
      this.patientDataInfo.referralAddress.locationTypeId =
        this.referralLocationFormGroup.get("locationType").value;
      this.patientDataInfo.referralAddress.phone =
        this.referralLocationFormGroup.get("referralPhone").value;
      this.patientDataInfo.referralAddress.emailAddress =
        this.referralLocationFormGroup.get("referralEmailAddress").value;
    }
  }

  clonePatientChartFromForm(clone: any, _patient: Patient) {
    this.cloneReferralLocationFromForm();

    const formatLookupValue = (id: any) => ({
      id: id || null,
      description: "",
      recertificationInterval: "",
    });

    let patientChart: any = {
      careType: clone.careType?.toUpperCase(),
      awareOfReferral: formatLookupValue(0),
      patient: {
        firstName: clone.firstName,
        lastName: clone.lastName,
        middleInitial: clone.middleInitial,
        nickName: clone.nickname,
        suffix: formatLookupValue(clone.suffix === 9 ? null : clone.suffix),
        prefix: formatLookupValue(clone.prefix === 7 ? null : clone.prefix),
        gender: formatLookupValue(clone.gender),
        birthDate: this.formatDate(clone.dateOfBirth),
        maritalStatus: formatLookupValue(clone.maritalStatus),
        firstLanguage: formatLookupValue(clone.firstLanguage),
        ethnicities: formatLookupValue(
          this.ethnicityRaceSyncService.calculateEthnicityOrRace(
            clone.ethnicity
          )
        ),
        hopeEthnicities: formatLookupValue(
          this.ethnicityRaceSyncService.calculateEthnicityOrRace(
            clone.hopeEthnicity
          )
        ),
        hopeRaces: formatLookupValue(
          this.ethnicityRaceSyncService.calculateEthnicityOrRace(clone.hopeRace)
        ),
        religion: formatLookupValue(clone.religion),
        religionOtherText: clone.religion == 20 ? clone.religionOtherText : "",
        emailAddress: clone.emailAddress,
        phoneNumber: {
          id: null,
          number: clone.phoneNumber,
          extension: "",
          phoneNumberType: 1,
        },
        homeAddress: {
          address1: clone.address1,
          address2: "",
          city: clone.city,
          state: clone.state,
          zip: clone.zip,
          zipExtension: clone.zipExtension,
          phone: clone.phoneNumber,
          email: clone.emailAddress,
          emailAddress: clone.emailAddress,
          county: clone.county,
          fax: "",
        },
        veteran: clone.veteran,
        uniqueIdentifier: clone.ssn,
      },
      patientStatus: this.resolvePatientStatusForSave(clone.type),
    };

    if (clone.sameAsHomeAddress) {
      patientChart.referralAddress = {
        address1: clone.address1 ? clone.address1 : "",
        city: clone.city ? clone.city : "",
        state: clone.state ? clone.state : "",
        zip: clone.zip ? clone.zip : "",
        zipExtension: clone.zipExtension ? clone.zipExtension : "",
        phone: clone.phone ? clone.phone : "",
        emailAddress: clone.emailAddress ? clone.emailAddress : "",
        county: clone.county ? clone.county : "",
        name: clone.referralName ? clone.referralName : "",
      };
    }
    if (this.patientDataInfo.referralAddress) {
      patientChart.referralAddress = this.patientDataInfo.referralAddress;
    }
    return patientChart;
  }

  getMatchingEthnicitiesIdsAndRacesIdsByDescription(
    source: any,
    items: any[],
    fieldType: string = "ethnicity"
  ): number[] {
    return this.ethnicityRaceSyncService.getMatchingEthnicitiesIdsAndRacesIdsByDescription(
      source,
      items,
      fieldType
    );
  }

  getReferralCreditContactFromForm(): Physician {
    const value = this.referralCreditFormGroup.value;
    return {
      ...this.referralCreditContact,
      // Required
      contactType: "REFERRAL_CREDIT",
      referralType: {
        id: 0,
      } as LookupValue,
      // From form
      referralCreditType: {
        ...(this.referralCreditContact
          ? this.referralCreditContact.referralCreditType
          : undefined),
        id: value.referralCreditType,
      },
      relationType: {
        ...(this.referralCreditContact
          ? this.referralCreditContact.relationType
          : undefined),
        id: value.referralCreditRelation,
      },
      firstName: value.referralCreditFirstName,
      lastName: value.referralCreditLastName,
      work: this.removeMask(value.referralCreditPhoneNumber),
      address: {
        address1: value.referralCreditStreetAddress
          ? value.referralCreditStreetAddress
          : "",
        address2: "",
        city: value.referralCreditCity ? value.referralCreditCity : "",
        state: value.referralCreditState ? value.referralCreditState : "",
        zip: value.referralCreditZip ? value.referralCreditZip : "",
        zipExtension: value.referralCreditZipExtension
          ? value.referralCreditZipExtension
          : "",
        phone: "",
        email: "",
        emailAddress: "",
        county: value.referralCreditCounty ? value.referralCreditCounty : "",
        fax: "",
      } as ContactDetails,
    };
  }

  // NOVA-1727: on an existing-patient profile save, only send patientStatus when the user changed
  // it; otherwise return null so the backend leaves the lifecycle status untouched.
  resolvePatientStatusForSave(cloneType) {
    const isUpdate = this.patientDataInfo?.id > 0;
    if (
      isUpdate &&
      !this.reinstatedReferral &&
      (cloneType ?? null) === (this.initialPatientStatusType ?? null)
    ) {
      return null;
    }
    return this.formatCloneType(cloneType);
  }

  formatCloneType(cloneType) {
    if (typeof cloneType === "string") {
      return (
        cloneType.charAt(0).toUpperCase() + cloneType.slice(1).toLowerCase()
      );
    }
    return cloneType;
  }

  formatDate(date): string {
    return date == null ? null : moment(date).format("YYYY-MM-DD");
  }

  handleReinstatedPatient(patientChart, referralCreditChanged) {
    return this.patientChartService
      .createPatientChartForPatient(
        this.patientDataInfo["patient"].id,
        patientChart
      )
      .pipe(
        mergeMap((createResponse: PatientChartResponse) => {
          const patientChartReinstated = {
            ...this.patientDataInfo,
            reinstated: true,
          };

          return this.patientChartService
            .newCreateOrUpdatePatientChart(
              this.patientDataInfo.id,
              patientChartReinstated,
              this.orderEntryToggle
            )
            .pipe(
              mergeMap((reinstatedResponse: PatientChartResponse) => {
                return this.patientChartService
                  .newCreateOrUpdatePatientChart(
                    createResponse.id,
                    patientChart,
                    this.orderEntryToggle
                  )
                  .pipe(
                    mergeMap((updateResponse: PatientChartResponse) => {
                      this.patientDataInfo.intakeNote = null;
                      return this.handleReferralCreditChange(
                        updateResponse,
                        reinstatedResponse,
                        referralCreditChanged
                      );
                    })
                  );
              })
            );
        })
      );
  }

  handleRegularPatient(patientChart, referralCreditChanged) {
    return this.patientChartService
      .newCreateOrUpdatePatientChart(
        this.patientDataInfo.id,
        patientChart,
        this.orderEntryToggle
      )
      .pipe(
        mergeMap((response: PatientChartResponse) =>
          this.handleReferralCreditChange(response, null, referralCreditChanged)
        )
      );
  }

  handleReferralCreditChange(
    updateResponse,
    reinstatedResponse,
    referralCreditChanged
  ) {
    return (
      referralCreditChanged
        ? this.resourceManagementService.createPhysician(
            updateResponse.id,
            this.getReferralCreditContactFromForm()
          )
        : of(undefined)
    ).pipe(
      map(
        (contact) =>
          [updateResponse, reinstatedResponse, contact] as [
            PatientChartResponse,
            PatientChartResponse,
            any
          ]
      ),
      catchError(() => {
        this.utilityService.presentCustomErrorToast(
          "Referral Credit info was not saved. Please try again later."
        );
        return of([updateResponse, reinstatedResponse, undefined] as [
          PatientChartResponse,
          PatientChartResponse,
          any
        ]);
      })
    );
  }

  handleRequests(requests, patientChart) {
    this.increaseSpinnerCount("Loading...");
    requests.subscribe(
      ([updateResponse, _reinstatedResponse, _contact]) => {
        this.decreaseSpinnerCount();
        this.presentToast("Patient Details Saved Successfully");
        this.referralService.CARE_TYPE = patientChart.careType;
        this.referralService.TENANT_ID = updateResponse.tenantId;
        this.referralService.PATIENT_STATUS = "referral";
        this.saveAdditionalData(updateResponse);
      },
      (err) => {
        console.error(err);
        this.decreaseSpinnerCount();
        this.referralService.presentToast("Error saving patient");
      }
    );
  }

  saveAdditionalData(response) {
    const saveIntakeNotesCall = this.setIntakeNotes(
      this.detailsFormGroup.controls.intakeNotes.value,
      response.id.toString()
    ).pipe(
      catchError((error) => {
        this.referralService.presentToast("Error saving notes");
        console.error("Error saving notes", error);
        return of(null);
      })
    );

    const saveCareTeamCall = this.saveCareTeam(
      response.externalId.toString()
    ).pipe(
      catchError((error) => {
        this.referralService.presentToast("Error saving care team");
        console.error("Error saving care team:", error);
        return of(null);
      })
    );

    this.increaseSpinnerCount();

    forkJoin([saveIntakeNotesCall, saveCareTeamCall]).subscribe({
      next: () => {},
      error: (err) => {
        console.error("Error saving patient data", err);
        this.decreaseSpinnerCount();
      },
      complete: () => {
        this.decreaseSpinnerCount();
        this.sendValue(response.id, response.externalId);
        this.detailsFormGroup.markAsPristine();
        this.referralLocationFormGroup.markAsPristine();
        this.referralLocationFormGroup.markAsUntouched();
        this.referralCreditFormGroup.markAsPristine();
        this.referralCreditFormGroup.markAsUntouched();
      },
    });
  }

  setIntakeNotes(note: string, patientChartId: string): Observable<any> {
    return new Observable<any>((observer) => {
      let data = {
        subject: "Intake Notes",
        note: note,
        patientChartId: patientChartId,
        type: {
          id: 6,
          description: "Intake Notes",
        },
      };
      if (this.patientDataInfo.intakeNote) {
        this.referralService
          .updateNote(data, this.patientDataInfo.intakeNote.id, patientChartId)
          .subscribe(
            (data) => {
              observer.next(data);
            },
            (err) => {
              observer.error(err);
            },
            () => {
              observer.complete();
            }
          );
      } else {
        this.referralService.createNote(data, patientChartId).subscribe(
          (data) => {
            observer.next(data);
          },
          (err) => {
            observer.error(err);
          },
          () => {
            observer.complete();
          }
        );
      }
    });
  }

  presentToast(message: string) {
    const toast = this.toastCtrl.create({
      message,
      duration: 3000,
      position: "below",
      cssClass: "success",
    });
    toast.present();
  }

  // incremental loader function
  increaseSpinnerCount(message: string = "Loading...") {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  // decremental loader function
  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  sendValue(patientChartId: number, patientChartExternalId: number) {
    this.valueChange.emit({
      patientChartId: patientChartId,
      patientExternalId: patientChartExternalId,
    });
  }

  touched(control: string) {
    return this.detailsFormGroup.get(control).touched;
  }

  valid(control: string) {
    return this.detailsFormGroup.get(control).valid;
  }

  protected readonly validationMessages = patientDetailsValidationMessage;
  protected readonly referralValidationMessages =
    referralLocationValidationMessage;

  setLookUpValues() {
    if (this.lookUpValues) {
      this.firstLanguages = this.lookUpValues["FirstLanguage"];
      this.religions = this.lookUpValues["Religion"];
      this.prefixes = this.lookUpValues["Prefix"];
      this.suffixes = this.lookUpValues["Suffix"];
      this.maritalStatuses = this.lookUpValues["MaritalStatus"];
      this.ethnicities = this.lookUpValues["Ethnicity"];
      this.hopeEthnicities = this.lookUpValues["HopeEthnicity"];
      this.hopeRaces = this.lookUpValues["HopeRace"];
      this.careLocationTypes = this.lookUpValues["ReferralLocationType"];
      this.careLocationTypes.sort((a, b) =>
        a.id < b.id ? -1 : a.id > b.id ? 1 : 0
      );
    }
    if (this.resourceManagementLookupValues) {
      this.referralCreditTypes =
        this.resourceManagementLookupValues.ReferralCreditType;
      this.referralCreditRelation =
        this.resourceManagementLookupValues.RelationType.filter(
          (relationType) => relationType.description !== "Physician"
        );
    }
  }

  onSearchForProviderOrCommunityResourceClick() {
    let selection = this.referralCreditFormGroup.get(
      "referralCreditRelation"
    ).value;
    // 2 is the ID for "Community Resource"; 10 is the ID for "Physician"
    if (selection === 2) {
      this.getCommunityResource();
    } else if (selection === 10) {
      this.searchForProviderOrCommunityResource(selection);
    } else {
      console.error(`Invalid selection: ${selection}`);
    }
  }

  clearProviderOrCommunityResourceSearchBoxOptions() {
    this.showProviderOrCommunityResourceSearchBox = false;
    this.labelForProviderOrCommunityResourceSearchBox = null;
    this.placeholderForProviderOrCommunityResourceSearchBox = "";
  }

  setProviderOrCommunityResourceSearchBoxOptions(
    e: CompanyResourceDropdownItem
  ) {
    const selection = e["description"];
    this.setDisplayOfProviderOrCommunityResourceSearchBox(selection);
    this.setLabelForProviderOrCommunityResourceSearchBox(selection);
    this.setPlaceholderForProviderOrCommunityResourceSearchBox(selection);
  }

  initializeProviderOrCommunityResourceSearchBox(relation?: string | number) {
    if (!relation) {
      return;
    }
    let relationType: string;
    if (typeof relation === "number") {
      relationType = relation === 2 ? "Community Resource" : "Physician";
    } else {
      relationType = relation;
    }
    this.setDisplayOfProviderOrCommunityResourceSearchBox(relationType);
    this.setLabelForProviderOrCommunityResourceSearchBox(relationType);
    this.setPlaceholderForProviderOrCommunityResourceSearchBox(relationType);
    this.setInitialProviderOrCommunityResourceSearchBoxValue();
  }

  setDisplayOfProviderOrCommunityResourceSearchBox(selection: string) {
    const approvedSearchBoxDescriptions = ["Community Resource", "Physician"];
    this.showProviderOrCommunityResourceSearchBox =
      approvedSearchBoxDescriptions.includes(selection);
  }

  setLabelForProviderOrCommunityResourceSearchBox(selection: string) {
    this.labelForProviderOrCommunityResourceSearchBox = selection;
  }

  setPlaceholderForProviderOrCommunityResourceSearchBox(selection: string) {
    this.placeholderForProviderOrCommunityResourceSearchBox = `Search For ${selection}`;
  }

  setInitialProviderOrCommunityResourceSearchBoxValue() {
    const initialSearchBoxValue = {
      address: this.referralCreditContact?.address,
      firstName: this.referralCreditContact?.firstName,
      label: this.referralCreditContact?.getFullName(),
      lastName: this.referralCreditContact?.lastName,
      phoneNumber: this.setProviderOrCommunityResourcePhoneNumber(
        this.referralCreditContact
      ),
      value: this.referralCreditContact?.id,
    };
    this.setProviderOrCommunityResource(initialSearchBoxValue);
  }

  setProviderOrCommunityResource(selection: CompanyResourceDropdownItem) {
    // Preset State
    const rcfgCntrls = this.referralCreditFormGroup.controls;
    const firstName = selection.firstName;
    const lastName = selection.lastName;
    const phoneNumber = selection.phoneNumber;
    const streetAddress = this.setProviderOrCommunityResourceStreetAddress(
      selection.address
    );
    const city = this.setProviderOrCommunityResourceCity(selection.address);
    const state = selection.address.state;
    const zipCode = this.setProviderOrCommunityResourceZipCode(
      selection.address
    );
    const zipCodeExtension =
      this.setProviderOrCommunityResourceZipCodeExtension(selection.address);
    const county = this.setProviderOrCommunityResourceCounty(selection.address);

    // Behavior
    rcfgCntrls.referralCreditFirstName.setValue(firstName);
    rcfgCntrls.referralCreditLastName.setValue(lastName);
    rcfgCntrls.referralCreditPhoneNumber.setValue(phoneNumber);
    rcfgCntrls.referralCreditStreetAddress.setValue(streetAddress);
    rcfgCntrls.referralCreditCity.setValue(city);
    rcfgCntrls.referralCreditState.setValue(state);
    rcfgCntrls.referralCreditZip.setValue(zipCode);
    rcfgCntrls.referralCreditZipExtension.setValue(zipCodeExtension);
    rcfgCntrls.referralCreditCounty.setValue(county);
  }

  clearProviderOrCommunityResourceSearchBox() {
    const rcfgCntrls = this.referralCreditFormGroup.controls;
    rcfgCntrls.referralCreditFirstName.setValue(null);
    rcfgCntrls.referralCreditLastName.setValue(null);
    rcfgCntrls.referralCreditPhoneNumber.setValue(null);
    rcfgCntrls.referralCreditStreetAddress.setValue(null);
    rcfgCntrls.referralCreditCity.setValue(null);
    rcfgCntrls.referralCreditState.setValue(null);
    rcfgCntrls.referralCreditZip.setValue(null);
    rcfgCntrls.referralCreditZipExtension.setValue(null);
    rcfgCntrls.referralCreditCounty.setValue(null);
  }

  setAllProvidersOrCommunityResources(selection: string) {
    this.allProvidersOrCommunityResourcesObservable$ =
      this.resourceManagementService
        .getCompanyResourceSummaries(
          0,
          10000,
          "",
          new SortOptions("last_name", "asc"),
          selection === "Physician"
            ? CompanyResourceType.PROVIDER
            : CompanyResourceType.COMMUNITY_RESOURCE
        )
        .pipe(
          mergeMap((companyResourceSummary) =>
            of(
              companyResourceSummary.content.map((individual) => {
                return individual;
              })
            )
          )
        );
    this.allProvidersOrCommunityResourcesObservable$.subscribe(
      (companyResourceSummaries) => {
        this.allProvidersOrCommunityResources = companyResourceSummaries.map(
          (x) => x
        );
      }
    );
  }

  getCommunityResource() {
    this.dropdownItemsProviderOrCommunityResource$ =
      this.queryProviderOrCommunityResource$.pipe(
        distinctUntilChanged(),
        switchMap((query: string | null) => {
          if (!query || query.length === 0) {
            return of([]);
          }
          return this.resourceManagementService
            .searchCommunityResources(query)
            .pipe(
              mergeMap((listOfCommunityResources) =>
                of(
                  listOfCommunityResources.map((communityResource) => {
                    return {
                      value: communityResource.id,
                      label: communityResource.getFullName(),
                      firstName: communityResource.firstName,
                      lastName: communityResource.lastName,
                      phoneNumber:
                        this.setProviderOrCommunityResourcePhoneNumber(
                          communityResource
                        ),
                      address: communityResource.address,
                    };
                  })
                )
              ),
              catchError(() => of([]))
            );
        })
      );
    this.dropdownItemsProviderOrCommunityResource$.subscribe(
      (dropdownItems) => {
        this.filteredProvidersOrCommunityResources = dropdownItems.map(
          (x) => x
        );
      }
    );
  }

  setProviderOrCommunityResourcePhoneNumber(
    resource: PhysicianCompanyResource
  ) {
    if (resource.phoneNumber) {
      return resource.phoneNumber;
    } else {
      return resource.mobile ? resource.mobile : resource.work;
    }
  }

  setProviderOrCommunityResourceStreetAddress(address: ContactDetails) {
    if (address.address2 && address.address2.trim().length > 0) {
      return `${address.address1}, ${address.address2}`;
    } else if (address.address1 && address.address1.trim().length > 0) {
      return `${address.address1}`;
    } else {
      return null;
    }
  }

  setProviderOrCommunityResourceCity(address: ContactDetails) {
    return address.city && address.city.trim().length > 0 ? address.city : null;
  }

  setProviderOrCommunityResourceZipCode(address: ContactDetails) {
    return address.zip && address.zip.trim().length > 0 ? address.zip : null;
  }

  setProviderOrCommunityResourceZipCodeExtension(address: ContactDetails) {
    return address.zipExtension && address.zipExtension.trim().length > 0
      ? address.zipExtension
      : null;
  }

  setProviderOrCommunityResourceCounty(address: ContactDetails) {
    return address.county && address.county.trim().length > 0
      ? address.county
      : null;
  }

  searchForProviderOrCommunityResource(selection: number) {
    this.dropdownItemsProviderOrCommunityResource$ =
      this.queryProviderOrCommunityResource$.pipe(
        distinctUntilChanged(),
        switchMap((query: string | null) => {
          if (!query || query.length === 0) {
            return of([]);
          }
          return this.resourceManagementService
            .getCompanyResourceSummaries(
              0,
              100,
              query ? query : undefined,
              new SortOptions("last_name", "asc"),
              selection === 10
                ? CompanyResourceType.PROVIDER
                : CompanyResourceType.COMMUNITY_RESOURCE
            )
            .pipe(
              mergeMap((listOfProvidersOrCommunityResources) =>
                of(
                  listOfProvidersOrCommunityResources.content.map(
                    (individual) => {
                      return {
                        label: individual.displayName,
                        value: individual.externalId,
                        firstName: individual.firstName,
                        lastName: individual.lastName,
                        phoneNumber: individual.phoneNumber,
                      };
                    }
                  )
                )
              ),
              catchError(() => of([]))
            );
        })
      );
    this.dropdownItemsProviderOrCommunityResource$.subscribe((items) => {
      this.filteredProvidersOrCommunityResources = items.map((x) => x);
    });
  }

  resetFacilityOptions() {
    this.facilityOptions = [];
    this.locationOptions = [];
  }

  setLocation(location: Facility) {
    this.referralLocationFormGroup.controls.referralName.setValue(null);
    this.referralLocationFormGroup.controls.address1Referral.setValue(null);
    this.referralLocationFormGroup.controls.referralCity.setValue(null);
    this.referralLocationFormGroup.controls.referralState.setValue(null);
    this.referralLocationFormGroup.controls.referralZip.setValue(null);
    this.referralLocationFormGroup.controls.referralZipExtension.setValue(null);
    this.referralLocationFormGroup.controls.referralCounty.setValue(null);
    this.referralLocationFormGroup.controls.referralPhone.setValue(null);
    this.referralLocationFormGroup.controls.referralEmailAddress.setValue(null);
    if (location) {
      if (location.name === "New Referral Location") {
        this.createNewReferralLocation(location.name);
        return;
      }
      this.showCloseIcon = true;
      this.updateCurrentLocationAddressOtherThanHome(location);
    }
  }
  createNewReferralLocation(_event) {
    this.enableCreateReferralLocation = true;
  }

  updateCurrentLocationAddressOtherThanHome(facility: Facility) {
    this.referralLocationFormGroup
      .get("locationTypeValue")
      .setValue(facility.name);
    this.patientDataInfo.referralAddress = ContactDetails.clone(
      facility.address
    );
    this.patientDataInfo.referralAddress.locationTypeId =
      this.referralLocationFormGroup.get("locationType").value;
    this.patientDataInfo.referralAddress.name = facility.name;
    this.showCurrentLocation = true;
  }

  subscribeToLocationTypeChanges() {
    this.locationChangeSubscription = this.referralLocationFormGroup
      .get("locationType")
      .valueChanges.subscribe(() => {
        this.referralLocationFormGroup.get("locationTypeValue").reset(null);
        this.referralLocationFormGroup.get("referralName").reset(null);
        this.referralLocationFormGroup.get("address1Referral").reset(null);
        this.referralLocationFormGroup.get("referralCity").reset(null);
        this.referralLocationFormGroup.get("referralState").reset(null);
        this.referralLocationFormGroup.get("referralZip").reset(null);
        this.referralLocationFormGroup.get("referralZipExtension").reset(null);
        this.referralLocationFormGroup.get("referralPhone").reset(null);
        this.referralLocationFormGroup.get("referralCounty").reset(null);
        this.referralLocationFormGroup.get("referralEmailAddress").reset(null);
      });
  }

  subscribeToLocationTypeValueChanges() {
    this.locationChangeSubscription = this.referralLocationFormGroup
      .get("locationTypeValue")
      .valueChanges.subscribe((value) => {
        this.showCurrentLocation = false;
        if (value === "New Referral Location") {
          this.enableFillReferralLocation = false;
          this.referralLocationFormGroup.controls.referralState.enable();
          this.patientDataInfo.referralAddress = new ContactDetails();
        } else {
          this.enableFillReferralLocation = true;
          this.referralLocationFormGroup.controls.referralState.disable();
        }
        this.referralLocationFormGroup.updateValueAndValidity();
      });
  }

  hasHomeAddress(): boolean {
    return (
      this.detailsFormGroup.get("address1").value !== "" &&
      this.detailsFormGroup.get("city").value !== "" &&
      this.detailsFormGroup.get("state").value !== "" &&
      this.detailsFormGroup.get("zip").value !== ""
    );
  }

  subscribeToSameAsChanges() {
    this.sameAsSubscription = this.referralLocationFormGroup
      .get("sameAsHomeAddress")
      .valueChanges.subscribe((x: boolean) => {
        if (x === true) {
          if (!this.hasHomeAddress()) {
            alert("Please Enter home address first");
            this.referralLocationFormGroup
              .get("sameAsHomeAddress")
              .setValue(false);
          } else {
            this.referralLocationFormGroup.get("locationType").disable();
            this.referralLocationFormGroup
              .get("locationTypeValue")
              .setValue(null, { emitEvent: false });
            this.referralLocationFormGroup.get("locationTypeValue").disable();
            this.showCurrentLocation = true;
            this.enableCreateReferralLocation = false;
            this.enableFillReferralLocation = false;
            this.locationOptions = [];
            this.homeSelected = true;
            this.patientDataInfo.referralAddress = new ContactDetails();
            this.patientDataInfo.referralAddress.address1 =
              this.detailsFormGroup.get("address1").value;
            this.patientDataInfo.referralAddress.city =
              this.detailsFormGroup.get("city").value;
            this.patientDataInfo.referralAddress.state =
              this.detailsFormGroup.get("state").value;
            this.patientDataInfo.referralAddress.zip =
              this.detailsFormGroup.get("zip").value;
            this.patientDataInfo.referralAddress.zipExtension =
              this.detailsFormGroup.get("zipExtension").value;
            this.patientDataInfo.referralAddress.county =
              this.detailsFormGroup.get("county").value;
            this.patientDataInfo.referralAddress.name = "Home";
            this.patientDataInfo.referralAddress.locationTypeId = null;
            this.patientDataInfo.referralAddress.phone =
              this.detailsFormGroup.get("phoneNumber").value;
            this.patientDataInfo.referralAddress.emailAddress =
              this.detailsFormGroup.get("emailAddress").value;
          }
        } else {
          this.homeSelected = false;
          this.showCurrentLocation = false;
          this.enableCreateReferralLocation = false;
          this.enableFillReferralLocation = false;
          this.locationOptions = [];
          this.referralLocationFormGroup.get("locationType").setValue(null);
          this.referralLocationFormGroup.get("locationType").enable();
          this.referralLocationFormGroup
            .get("locationTypeValue")
            .setValue(null, { emitEvent: false });
          this.referralLocationFormGroup.get("locationTypeValue").enable();
          this.patientDataInfo.referralAddress = null;
        }
      });
  }

  searchCounty() {
    let zip = "";
    this.hideCounty = true;
    if (!this.patientDataInfo.homeAddress)
      this.patientDataInfo.homeAddress = new ContactDetails();
    let zipCode = this.detailsFormGroup.get("zip").value;
    const countyFormControl = this.detailsFormGroup.controls.county;

    if (this.checkIfValidZip()) {
      countyFormControl.setValidators([Validators.required]);
      zip = zipCode;
      const state = this.detailsFormGroup.get("state").value;

      this.facilitiesService.getCountyByStateAndZipCode(state, zip).subscribe(
        (response) => {
          let counties;
          if (Array.isArray(response)) {
            counties = response as any[];
          } else {
            counties = [response];
          }

          if (counties.length === 1) {
            this.detailsFormGroup.controls["county"].setValue(
              response[0]["county"]
            );
            this.countyOption = counties;
            this.countyFetchError = "";
          } else {
            this.countyOption = response;
            this.countyFetchError = "";
            this.hideCounty = false;
          }

          this.noCountiesReturned = counties.length === 0;
        },
        (err) => {
          this.countyOption = [];
          console.error(err);
          this.noCountiesReturned = true;
          countyFormControl.setValidators([]); // set county as NOT required in case of error response
        }
      );
    } else {
      this.countyOption = [];
      countyFormControl.setValue("");
      countyFormControl.setValidators([]); // set county as NOT required if zip is not valid
    }
    countyFormControl.updateValueAndValidity();
  }
  searchCountyReferral() {
    let zipReferral = "";
    this.hideCountyReferral = true;
    let zipCodeReferral =
      this.referralLocationFormGroup.get("referralZip").value;
    const countyFormControl =
      this.referralLocationFormGroup.controls.referralCounty;

    if (this.checkIfValidZipReferral()) {
      countyFormControl.setValidators([Validators.required]);
      zipReferral = zipCodeReferral;
      const stateReferral =
        this.referralLocationFormGroup.get("referralState").value;

      this.facilitiesService
        .getCountyByStateAndZipCode(stateReferral, zipReferral)
        .subscribe(
          (response) => {
            let counties;
            if (Array.isArray(response)) {
              counties = response as any[];
            } else {
              counties = [response];
            }

            if (counties.length === 1) {
              this.referralLocationFormGroup.controls[
                "referralCounty"
              ].setValue(response[0]["county"]);
              this.countyOptionReferral = counties;
              this.countyFetchErrorReferral = "";
            } else {
              this.countyOptionReferral = response;
              this.countyFetchErrorReferral = "";
              this.hideCountyReferral = false;
            }

            this.noCountiesReturnedReferral = counties.length === 0;
          },
          (err) => {
            this.countyOptionReferral = [];
            console.error(err);
            this.noCountiesReturnedReferral = true;
            countyFormControl.setValidators([]); // set county as NOT required in case of error response
          }
        );
    } else {
      this.countyOptionReferral = [];
      countyFormControl.setValue("");
      countyFormControl.setValidators([]); // set county as NOT required if zip is not valid
    }
    countyFormControl.updateValueAndValidity();
  }

  searchCountyReferralCredit() {
    let zipReferralCredit = "";
    this.hideCountyReferralCredit = true;
    let zipCodeReferralCredit =
      this.referralCreditFormGroup.get("referralCreditZip").value;
    const countyFormControl =
      this.referralCreditFormGroup.controls.referralCreditCounty;

    if (this.checkIfValidZipReferralCredit()) {
      countyFormControl.setValidators([Validators.required]);
      zipReferralCredit = zipCodeReferralCredit;
      const stateReferralCredit = this.referralCreditFormGroup.get(
        "referralCreditState"
      ).value;

      this.facilitiesService
        .getCountyByStateAndZipCode(stateReferralCredit, zipReferralCredit)
        .subscribe(
          (response) => {
            let counties;
            if (Array.isArray(response)) {
              counties = response as any[];
            } else {
              counties = [response];
            }

            if (counties.length === 1) {
              this.referralCreditFormGroup.controls[
                "referralCreditCounty"
              ].setValue(response[0]["county"]);
              this.countyOptionReferralCredit = counties;
              this.countyFetchErrorReferralCredit = "";
            } else {
              this.countyOptionReferralCredit = response;
              this.countyFetchErrorReferralCredit = "";
              this.hideCountyReferralCredit = false;
            }

            this.noCountiesReturnedReferralCredit = counties.length === 0;
          },
          (err) => {
            this.countyOptionReferralCredit = [];
            console.error(err);
            this.noCountiesReturnedReferralCredit = true;
            countyFormControl.setValidators([]); // set county as NOT required in case of error response
          }
        );
    } else {
      this.countyOptionReferralCredit = [];
      countyFormControl.setValue("");
      countyFormControl.setValidators([]); // set county as NOT required if zip is not valid
    }
    countyFormControl.updateValueAndValidity();
  }
  checkIfValidCountyInput() {
    const countyFormControl = this.detailsFormGroup.controls.county;
    if (!this.checkIfValidZip()) {
      countyFormControl.setValidators([]);
      countyFormControl.updateValueAndValidity();
    } else {
      if (_.isEmpty(countyFormControl.value)) {
        countyFormControl.setValidators([Validators.required]);
        countyFormControl.updateValueAndValidity();
      }
    }
  }
  checkIfValidCountyReferralInput() {
    const countyFormControl = this.detailsFormGroup.controls.referralCounty;
    if (!this.checkIfValidZipReferral()) {
      countyFormControl.setValidators([]);
      countyFormControl.updateValueAndValidity();
    } else {
      if (_.isEmpty(countyFormControl.value)) {
        countyFormControl.setValidators([Validators.required]);
        countyFormControl.updateValueAndValidity();
      }
    }
  }

  onCountyClick() {
    if (this.checkIfValidZip()) {
      // fetch counties from back-end if we haven't yet.
      if (this.countyOption.length === 0 && !this.noCountiesReturned) {
        this.searchCounty();
      }
      // otherwise, display already-loaded county options
      else {
        this.hideCounty = false;
      }
    }
  }
  onCountyReferralClick() {
    if (this.checkIfValidZipReferral()) {
      // fetch counties from back-end if we haven't yet.
      if (
        this.countyOptionReferral.length === 0 &&
        !this.noCountiesReturnedReferral
      ) {
        this.searchCountyReferral();
      }
      // otherwise, display already-loaded county options
      else {
        this.hideCountyReferral = false;
      }
    }
  }
  onCountyClickReferralCredit() {
    if (this.checkIfValidZipReferralCredit()) {
      // fetch counties from back-end if we haven't yet.
      if (
        this.countyOptionReferralCredit.length === 0 &&
        !this.noCountiesReturnedReferralCredit
      ) {
        this.searchCountyReferralCredit();
      }
      // otherwise, display already-loaded county options
      else {
        this.hideCountyReferralCredit = false;
      }
    }
  }
  checkIfValidZip(zipCode: string = this.detailsFormGroup.controls.zip.value) {
    this.hasValidZip = zipCode && zipCode.length === 5 && /^\d+$/.test(zipCode);
    return this.hasValidZip;
  }
  setCounty(county) {
    this.detailsFormGroup.controls["county"].setValue(county.county);
    this.hideCounty = true;
  }
  checkIfValidZipReferral(
    zipCode: string = this.referralLocationFormGroup.controls.referralZip.value
  ) {
    this.hasValidZipReferral =
      zipCode && zipCode.length === 5 && /^\d+$/.test(zipCode);
    return this.hasValidZipReferral;
  }

  setReferralCounty(county) {
    this.referralLocationFormGroup.controls["referralCounty"].setValue(
      county.county
    );
    this.hideCountyReferral = true;
  }

  checkIfValidZipReferralCredit(
    zipCode: string = this.referralCreditFormGroup.controls.referralCreditZip
      .value
  ) {
    this.hasValidZipReferralCredit =
      zipCode && zipCode.length === 5 && /^\d+$/.test(zipCode);
    return this.hasValidZipReferralCredit;
  }
  setCountyReferralCredit(county) {
    this.referralCreditFormGroup.controls["referralCreditCounty"].setValue(
      county.county
    );
    this.hideCountyReferralCredit = true;
  }

  mapFacilityAndReferralLocation(locationTypeValue) {
    this.facilityOptions = null;

    this.patientChartService
      .searchReferralLocationByType("", locationTypeValue, 0, 9999)
      .pipe()
      .take(1)
      .subscribe((facilities: ContentWrapper<Facility>) => {
        this.facilityOptions = facilities.content;
        this.enableCreateReferralLocation = false;

        let facilitiesSubLookUp = this.careLocationTypes.filter(
          (x) =>
            x.id == this.referralLocationFormGroup.get("locationType").value
        )[0];

        if (
          facilitiesSubLookUp.hasOwnProperty("subLookup") &&
          facilitiesSubLookUp.subLookup.facilities !== null
        ) {
          let facilitiesArray = facilitiesSubLookUp.subLookup.facilities;
          for (let i = 0; i < facilitiesArray.length; i++) {
            if (this.newBillingFacilityToggle) {
              this.getBillingFacilities(facilitiesArray[i].id);
            } else {
              this.facilitiesService
                .searchFacilitiesByType("", `${facilitiesArray[i].id}`, 0, 9999)
                .pipe()
                .take(1)
                .subscribe((facilities: ContentWrapper<Facility>) => {
                  for (let j = 0; j < facilities.content.length; j++) {
                    this.facilityOptions.push(facilities.content[j]);
                  }
                });
            }
          }
        }
      });
  }

  mapBillingFacility(facilityData, facilityType) {
    let ofList = [];
    facilityData["hits"].forEach((element) => {
      let newFacility: FacilityDetail;
      let of: Facility = {} as any as Facility;
      newFacility = element;
      if (newFacility.facilityId) {
        of.id = newFacility.facilityId;
        of.name = newFacility.facilityName;
        of.facilityType = facilityType;
        this.formatNumberAddress(newFacility, of);
        ofList.push(of);
      }
    });
    return ofList;
  }

  formatNumberAddress(value, oldFacility) {
    let facilityDetails: ContactDetails = {} as any as ContactDetails;

    if (value && value.facilityContacts && value.facilityContacts.length > 0) {
      value.facilityContacts.forEach((element) => {
        facilityDetails.phone = element.phone || "";
        facilityDetails.email = element.email || "";
        facilityDetails.fax = element.faxNumber
          ? String(element.faxNumber)
          : "";
      });
    } else {
      if (value.isLegacy && value.isActive) {
        facilityDetails.phone =
          value.phones && value.phones.length > 0
            ? this.getPhoneNumber(value.phones, "OFFICE") ||
              this.getFirstPhoneNumber(value.phones) ||
              ""
            : "";
        facilityDetails.fax =
          value.phones && value.phones.length > 0
            ? this.getPhoneNumber(value.phones, "FAX") || ""
            : "";
      } else {
        facilityDetails.phone =
          value.phones && value.phones.length > 0
            ? this.getFirstPhoneNumber(value.phones) || ""
            : "";
      }
    }

    facilityDetails.address1 = value.streetAddress.address1;
    facilityDetails.address2 = value.streetAddress.address2;
    facilityDetails.city = value.streetAddress.city;
    facilityDetails.state = value.streetAddress.state;
    facilityDetails.county = value.streetAddress.county;
    if (
      value.streetAddress.zipCode &&
      value.streetAddress.zipCode.length == 9
    ) {
      const zip = value.streetAddress.zipCode.substring(0, 5);
      const zipCodeExt = value.streetAddress.zipCode.substring(5, 9);
      facilityDetails.zip = zip;
      facilityDetails.zipExtension = zipCodeExt;
    } else {
      facilityDetails.zip = value.streetAddress.zipCode;
    }
    oldFacility.address = facilityDetails;
  }

  getPhoneNumber(phones, type) {
    if (!phones || phones.length === 0) {
      return null;
    }
    const phone = phones.filter((x) => x.type === type)[0];
    return phone ? phone.number : null;
  }

  getFirstPhoneNumber(phones) {
    if (!phones || phones.length === 0) {
      return null;
    }
    return phones[0].number || null;
  }

  getBillingFacilities(id) {
    let ofList = [];
    let facilityType = this.getFacilityType(id);
    let details = this.createRequest(facilityType);
    this.facilitiesService
      .getFacilityFromOpenSearch(details)
      .subscribe((facilityData) => {
        if (
          facilityData !== null &&
          facilityData["hits"] !== null &&
          facilityData["hits"].length > 0
        ) {
          ofList = this.mapBillingFacility(facilityData, facilityType);
          if (ofList.length > 0) {
            for (let j = 0; j < ofList.length; j++) {
              this.facilityOptions.push(ofList[j]);
            }
          }
        }
      });
    return ofList;
  }
  createRequest(type) {
    let details: any = {};
    details.DataType = "FACILITY";
    details.mustFields = [
      {
        name: "dataType",
        value: "FACILITY",
      },
      {
        name: "isActive",
        value: "true",
      },
      {
        name: "facilityType",
        value: type,
      },
    ];
    details.SearchString = null;
    details.dateRange = null;
    details.OptionalFields = [];
    details.sortBy = {
      name: "facilityName",
      value: "asc",
    };
    details.From = 0;
    details.Size = 9999;
    return details;
  }

  getFacilityType(id) {
    let facilities = this.facilityLocation;
    return facilities.filter((x) => x.id == id)[0]
      ? facilities.filter((x) => x.id == id)[0].value
      : "";
  }

  onDismiss() {
    this.detailsFormGroup.reset();
    if (
      // If there's a referral address that's only saved on this.patientDataInfo,
      // but not in the form it breaks if you reset it.
      // We might want to rethink, like creating a hidden form to hold the info,
      // this if it becomes a problem.
      !this.showCurrentLocation
    ) {
      this.referralLocationFormGroup.reset();
    }
    this.referralCreditFormGroup.reset();
    if (this.patientDataInfo) {
      this.amendForm();
    }
    this.detailsFormGroup.markAsPristine();
    this.referralLocationFormGroup.markAsPristine();
    this.referralCreditFormGroup.markAsPristine();
  }

  saveCareTeam(patientExternalId: string): Observable<any> {
    return new Observable((observer) => {
      const payload = {
        teamId: this.detailsFormGroup.controls.team.value,
        members: [],
      };
      // Check if we need to create a new one or update an existing one
      if (
        !(
          this.patientDataInfo &&
          this.patientDataInfo.teamInfo &&
          this.patientDataInfo.teamInfo.team &&
          this.patientDataInfo.teamInfo.team.id
        ) &&
        this.detailsFormGroup.controls.team.value
      ) {
        this.careTeamsService
          .postCareTeamMembers(patientExternalId, payload, false.toString())
          .subscribe(
            (data) => {
              observer.next(data);
            },
            (error) => {
              observer.error(error);
            },
            () => {
              observer.complete();
            }
          );
      } else if (
        this.patientDataInfo &&
        this.patientDataInfo.teamInfo &&
        this.patientDataInfo.teamInfo.team &&
        this.patientDataInfo.teamInfo.team.id &&
        this.detailsFormGroup.controls.team.value !=
          this.patientDataInfo.teamInfo.team.id
      ) {
        this.careTeamsService
          .putCareTeamMembers(patientExternalId, payload, false.toString())
          .subscribe(
            (data) => {
              observer.next(data);
            },
            (error) => {
              observer.error(error);
            },
            () => {
              observer.complete();
            }
          );
      }

      observer.next();
      observer.complete();
    });
  }

  loadCareTeams() {
    this.increaseSpinnerCount("Loading teams...");
    this.careTeamsService.getAllTeams(1000).subscribe((data) => {
      this.decreaseSpinnerCount();
      const responseTeams: any = data;
      this.careTeams = responseTeams.content;
      this.careTeams.sort((ct1, ct2) => ct1.name.localeCompare(ct2.name));
    });
  }

  fieldHasError(field: string, type: string) {
    const control = this.detailsFormGroup.get(field);
    return control?.hasError(type) && (control.dirty || control.touched);
  }

  // ===================== Submit logic ===================== /

  preparePatientChart() {
    let patientChart = this.clonePatientChartFromForm(
      this.detailsFormGroup.value,
      this.patientDataInfo
    );

    patientChart.patient.uniqueIdentifier = this.removeMask(
      patientChart.patient.uniqueIdentifier
    );

    if (this.detailsFormGroup.value.unknownSSN) {
      patientChart.patient.uniqueIdentifier = "";
      patientChart.patient.ssnUnknown = true;
    } else {
      patientChart.patient.ssnUnknown = false;
    }

    patientChart.patient.phoneNumber.number = this.removeMask(
      patientChart.patient.phoneNumber.number
    );

    return patientChart;
  }

  disableSubmit(): boolean {
    let disableForDetails = false;
    let disableForReferralLocation = false;
    let disableForReferralCredit = false;

    if (this.detailsFormGroup.pristine) {
      disableForDetails = true;
    }
    if (!this.detailsFormGroup.valid) {
      disableForDetails = true;
    } else if (
      _.isEqual(this.originalDetailsFormValues, this.detailsFormGroup.value)
    ) {
      disableForDetails = true;
    }
    if (this.detailsFormGroup.valid) {
      if (this.referralLocationFormGroup.touched) {
        if (
          !_.isEqual(
            this.originalReferralLocationFormValues,
            this.referralLocationFormGroup.value
          )
        ) {
          disableForDetails = false;
          // New Referral Location
          if (
            this.referralLocationFormGroup.get("locationTypeValue").value ===
            "New Referral Location"
          ) {
            if (!this.referralLocationFormGroup.valid) {
              disableForReferralLocation = true;
            }
          }
        }
      }
    }
    if (this.detailsFormGroup.valid) {
      if (this.referralCreditFormGroup.touched) {
        if (
          !_.isEqual(
            this.originalReferralCreditFormValues,
            this.referralCreditFormGroup.value
          )
        ) {
          disableForDetails = false;
          if (!this.referralCreditFormGroup.valid) {
            disableForReferralCredit = true;
          }
        }
      }
    }
    return (
      disableForDetails ||
      disableForReferralLocation ||
      disableForReferralCredit
    );
  }

  onSubmit() {
    const patientChart = this.preparePatientChart();
    if (this.detailsFormGroup.valid) {
      const referralCreditChanged = this.referralCreditFormGroup.dirty;

      const requests =
        this.reinstatedReferral &&
        this.tabStatuses[IntakePatientProfileTab.PATIENT_DETAILS] === "blank"
          ? this.handleReinstatedPatient(patientChart, referralCreditChanged)
          : this.handleRegularPatient(patientChart, referralCreditChanged);

      this.handleRequests(requests, patientChart);
    } else {
      console.error(this.detailsFormGroup.errors);
    }
  }

  get isOtherReligionSelected() {
    return this.detailsFormGroup.get("religion").value == 20;
  }
}



// WEBPACK FOOTER //
// ./src/components/intake-queue/patient-details-grid/patient-details-grid.ts